import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import AdminLayout from "@/components/admin/AdminLayout";
import { BrutalButton } from "@/components/ui/BrutalButton";
import { BrutalInput } from "@/components/ui/BrutalInput";
import { BrutalCard } from "@/components/ui/BrutalCard";
import KPICard from "@/components/admin/KPICard";
import { Switch } from "@/components/ui/switch";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";
import { ArrowLeft, Plus, Pencil, Trash2, Search, Tags, ChevronLeft, ChevronRight } from "lucide-react";
import { toast } from "@/hooks/use-toast";
import { CategoryFormModal } from "@/components/admin/CategoryFormModal";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

interface Category {
  id: string;
  icon: string;
  name_it: string;
  name_en: string | null;
  name_es: string | null;
  keywords_it: string[] | null;
  keywords_en: string[] | null;
  keywords_es: string[] | null;
  display_order: number;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

const AdminCategories = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [search, setSearch] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const [editingCategory, setEditingCategory] = useState<Category | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [categoryToDelete, setCategoryToDelete] = useState<Category | null>(null);
  const [usageStats, setUsageStats] = useState<{ profiles: number; companies: number }>({ profiles: 0, companies: 0 });
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(25);

  const currentLanguage = (i18n.language || 'it').substring(0, 2) as 'it' | 'en' | 'es';

  const getLocalizedName = (cat: Category) => {
    switch (currentLanguage) {
      case 'en': return cat.name_en || cat.name_it;
      case 'es': return cat.name_es || cat.name_it;
      default: return cat.name_it;
    }
  };

  // Reset page when search changes
  useEffect(() => {
    setCurrentPage(1);
  }, [search]);

  const { data: categories = [], isLoading, error } = useQuery({
    queryKey: ["admin-categories"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("professional_categories")
        .select("*")
        .order("display_order", { ascending: true });
      if (error) throw error;
      return data as Category[];
    },
  });

  const toggleActiveMutation = useMutation({
    mutationFn: async ({ id, is_active }: { id: string; is_active: boolean }) => {
      const { error } = await supabase
        .from("professional_categories")
        .update({ is_active })
        .eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-categories"] });
      toast({ title: t("admin.categories.updateSuccess") });
    },
    onError: () => {
      toast({ title: t("admin.categories.updateError"), variant: "error" });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.functions.invoke("delete-category", {
        body: { categoryId: id },
      });
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-categories"] });
      toast({ title: t("admin.categories.deleteSuccess") });
      setDeleteDialogOpen(false);
      setCategoryToDelete(null);
    },
    onError: () => {
      toast({ title: t("admin.categories.deleteError"), variant: "error" });
    },
  });

  const handleDelete = async (category: Category) => {
    // Check usage
    const [profilesRes, companiesRes] = await Promise.all([
      supabase.from("profiles").select("id", { count: "exact" }).contains("categories", [category.id]),
      supabase.from("companies").select("id", { count: "exact" }).contains("categories", [category.id]),
    ]);
    setUsageStats({
      profiles: profilesRes.count || 0,
      companies: companiesRes.count || 0,
    });
    setCategoryToDelete(category);
    setDeleteDialogOpen(true);
  };

  const filteredCategories = categories.filter(
    (cat) =>
      cat.id.toLowerCase().includes(search.toLowerCase()) ||
      cat.name_it.toLowerCase().includes(search.toLowerCase()) ||
      (cat.name_en?.toLowerCase().includes(search.toLowerCase()) ?? false) ||
      (cat.name_es?.toLowerCase().includes(search.toLowerCase()) ?? false)
  );

  const activeCount = categories.filter((c) => c.is_active).length;

  // Pagination
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedCategories = filteredCategories.slice(startIndex, endIndex);
  const totalPages = Math.ceil(filteredCategories.length / itemsPerPage);

  if (error) {
    return (
      <AdminLayout>
        <div className="p-8 text-destructive">{t("admin.categories.loadError")}</div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="p-8 space-y-6">
        {/* Breadcrumb */}
        <Breadcrumb className="mb-6">
          <BreadcrumbList>
            <BreadcrumbItem>
              <BreadcrumbLink href="/admin/dashboard">Dashboard</BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem>
              <BreadcrumbLink href="/admin/settings">{t("admin.settings.title")}</BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem>
              <BreadcrumbPage>{t("admin.categories.title")}</BreadcrumbPage>
            </BreadcrumbItem>
          </BreadcrumbList>
        </Breadcrumb>

        {/* Header */}
        <div className="flex items-center gap-4 mb-8">
          <button
            onClick={() => navigate("/admin/dashboard")}
            className="p-2 hover:bg-accent transition-colors rounded-lg"
            aria-label={t("admin.categories.backButton")}
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div>
            <h1 className="font-heading text-4xl font-bold flex items-center gap-3">
              <Tags className="w-10 h-10 text-primary" />
              {t("admin.categories.title")}
            </h1>
            <p className="text-muted-foreground mt-1">{t("admin.categories.subtitle")}</p>
          </div>
        </div>

        {/* Action Button */}
        <div className="mb-6">
          <BrutalButton onClick={() => { setEditingCategory(null); setModalOpen(true); }}>
            <Plus className="h-5 w-5 mr-2" />
            {t("admin.categories.addCategory")}
          </BrutalButton>
        </div>

        {/* KPIs */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <KPICard title={t("admin.categories.kpi.total")} value={categories.length} />
          <KPICard title={t("admin.categories.kpi.active")} value={activeCount} />
          <KPICard title={t("admin.categories.kpi.inactive")} value={categories.length - activeCount} />
        </div>

        {/* Search */}
        <BrutalCard className="p-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <BrutalInput
              placeholder={t("admin.categories.searchPlaceholder")}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10"
            />
          </div>
        </BrutalCard>

        {/* Table */}
        <BrutalCard>
          {isLoading ? (
            <div className="p-8 text-center text-muted-foreground">{t("common.loading")}</div>
          ) : filteredCategories.length === 0 ? (
            <div className="p-8 text-center">
              <p className="text-muted-foreground">{t("admin.categories.noCategories")}</p>
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-surface border-b-[3px] border-dark">
                    <tr>
                      <th className="px-6 py-4 text-left font-heading font-bold w-[80px]">{t("admin.categories.table.order")}</th>
                      <th className="px-6 py-4 text-left font-heading font-bold">{t("admin.categories.table.name")}</th>
                      <th className="px-6 py-4 text-left font-heading font-bold">{t("admin.categories.table.id")}</th>
                      <th className="px-6 py-4 text-left font-heading font-bold w-[80px]">{t("admin.categories.table.active")}</th>
                      <th className="px-6 py-4 text-left font-heading font-bold w-[100px]">{t("admin.categories.table.actions")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {paginatedCategories.map((cat) => (
                      <tr key={cat.id} className="border-b-[3px] border-dark hover:bg-surface transition-colors">
                        <td className="px-6 py-4 font-mono text-sm">{cat.display_order}</td>
                        <td className="px-6 py-4">
                          <span className="text-xl mr-2">{cat.icon}</span>
                          <span className="font-medium">{getLocalizedName(cat)}</span>
                        </td>
                        <td className="px-6 py-4 font-mono text-sm text-muted-foreground">{cat.id}</td>
                        <td className="px-6 py-4">
                          <Switch
                            checked={cat.is_active}
                            onCheckedChange={(checked) => toggleActiveMutation.mutate({ id: cat.id, is_active: checked })}
                          />
                        </td>
                        <td className="px-6 py-4">
                          <TooltipProvider>
                            <div className="flex gap-2">
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <button
                                    onClick={() => { setEditingCategory(cat); setModalOpen(true); }}
                                    className="p-2 border-[3px] border-dark bg-card shadow-[4px_4px_0px_0px] shadow-dark hover:translate-x-[1px] hover:translate-y-[1px] hover:shadow-[3px_3px_0px_0px] hover:shadow-dark transition-all"
                                  >
                                    <Pencil className="h-4 w-4" />
                                  </button>
                                </TooltipTrigger>
                                <TooltipContent>{t("common.edit")}</TooltipContent>
                              </Tooltip>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <button
                                    onClick={() => handleDelete(cat)}
                                    className="p-2 border-[3px] border-dark bg-destructive text-white shadow-[4px_4px_0px_0px] shadow-dark hover:translate-x-[1px] hover:translate-y-[1px] hover:shadow-[3px_3px_0px_0px] hover:shadow-dark transition-all"
                                  >
                                    <Trash2 className="h-4 w-4" />
                                  </button>
                                </TooltipTrigger>
                                <TooltipContent>{t("common.delete")}</TooltipContent>
                              </Tooltip>
                            </div>
                          </TooltipProvider>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination Footer */}
              <div className="px-6 py-4 border-t-[3px] border-dark flex flex-col sm:flex-row items-center justify-between gap-4">
                <div className="text-sm text-muted-foreground">
                  {t("admin.categories.showing", {
                    start: startIndex + 1,
                    end: Math.min(endIndex, filteredCategories.length),
                    total: filteredCategories.length
                  })}
                </div>
                
                <div className="flex items-center gap-4">
                  <div className="flex items-center gap-2">
                    <span className="text-sm">{t("admin.categories.rowsPerPage")}</span>
                    <select
                      value={itemsPerPage}
                      onChange={(e) => {
                        setItemsPerPage(Number(e.target.value));
                        setCurrentPage(1);
                      }}
                      className="border-[3px] border-dark px-2 py-1 bg-card font-medium rounded-none"
                    >
                      <option value={25}>25</option>
                      <option value={50}>50</option>
                      <option value={100}>100</option>
                      <option value={200}>200</option>
                    </select>
                  </div>
                  
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                      disabled={currentPage === 1}
                      className="p-2 border-[3px] border-dark bg-card disabled:opacity-50 disabled:cursor-not-allowed hover:bg-surface transition-colors"
                    >
                      <ChevronLeft className="h-4 w-4" />
                    </button>
                    <span className="text-sm font-medium min-w-[60px] text-center">
                      {currentPage} / {totalPages || 1}
                    </span>
                    <button
                      onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                      disabled={currentPage >= totalPages}
                      className="p-2 border-[3px] border-dark bg-card disabled:opacity-50 disabled:cursor-not-allowed hover:bg-surface transition-colors"
                    >
                      <ChevronRight className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              </div>
            </>
          )}
        </BrutalCard>
      </div>

      {/* Modal */}
      <CategoryFormModal
        open={modalOpen}
        onOpenChange={setModalOpen}
        category={editingCategory}
        onSuccess={() => {
          queryClient.invalidateQueries({ queryKey: ["admin-categories"] });
          setModalOpen(false);
          setEditingCategory(null);
        }}
      />

      {/* Delete Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("admin.categories.deleteCategory")}</AlertDialogTitle>
            <AlertDialogDescription>
              {t("admin.categories.deleteConfirm")}
              {(usageStats.profiles > 0 || usageStats.companies > 0) && (
                <span className="block mt-2 text-amber-600">
                  {t("admin.categories.deleteWarning", usageStats)}
                </span>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t("common.cancel")}</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => categoryToDelete && deleteMutation.mutate(categoryToDelete.id)}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {t("common.delete")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </AdminLayout>
  );
};

export default AdminCategories;
